import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Policy Checking agent's screens. A check is one policy's paperwork — the
 * quote, the application and the issued policy — read and compared. The
 * insured's name arrives in full, in lists as well as on opening.
 */

export type PcStatus = 'reading' | 'checked' | 'failed';
export type PcResult = 'must_fix' | 'review' | 'all_clear';
export type PcSeverity = 'must_fix' | 'review' | 'match';
export type PcKind = 'quote' | 'application' | 'policy' | 'unknown';

export interface PcCheckCard {
  id: string;
  created_at: string;
  checked_at: string | null;
  by: string | null;
  status: PcStatus;
  result: PcResult | null;
  /** Where it is, in words: "Reading the documents", "Must fix". */
  stage: string;
  policy_num: string | null;
  carrier: string | null;
  product: string | null;
  insured: string | null;
  face_amount: number | null;
  must_fix: number;
  review: number;
  matched: number;
  document_count: number;
  kinds: PcKind[];
  missing: PcKind[];
  error: string | null;
  source: 'live' | 'seed';
}

export interface PcDocument {
  id: string;
  filename: string;
  kind: PcKind;
  kind_label: string;
  status: 'uploaded' | 'read' | 'failed';
  pages: number | null;
  size_bytes: number;
  error: string | null;
  summary: string;
  uploaded_at: string;
}

export interface PcFinding {
  area: string;
  label: string;
  severity: PcSeverity;
  /** What each column said, by column key. */
  values: Record<string, string | null>;
  detail: string | null;
}

export interface PcCheckDetail extends PcCheckCard {
  insured_name: string | null;
  columns: { key: string; label: string }[];
  documents: PcDocument[];
  findings: PcFinding[];
  records: Record<string, unknown> | null;
}

export interface PcDay { day: string; value: number }

export interface PcSpotlight {
  flow: { documents_this_week: number; last_run_at: string | null; error: string | null; reading_now: number };
  tiles: {
    /** The small charts: the last 7 days, day by day. */
    checks: PcDay[]; must_fix: PcDay[]; review: PcDay[]; all_clear: PcDay[];
    /** The headline figures: everything the agent has checked so far. */
    totals: {
      checked: number;
      problems: number;
      with_a_problem: number;
      cover_at_risk: number;
      fields_checked: number;
    };
    documents: number;
  };
  caught: { label: string; value: number }[];
  results: { label: string; value: number }[];
}

export interface PcConfiguration {
  checks: { area: string; label: string; compares: string; severity: PcSeverity; severity_label: string }[];
  severities: { key: PcSeverity; label: string; detail: string }[];
  reads: { kind: string; label: string; detail: string }[];
  rules: { when: string; then: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
  limits: { max_files: number; max_mb: number; accepted: string };
}

export interface PcIntegration {
  id: string;
  name: string;
  role: string;
  connected: boolean;
  detail?: string;
  checked_at?: string | null;
  error?: string | null;
}

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { ...(json && { 'Content-Type': 'application/json' }), ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/policy-checking`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: authHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export const fetchChecks = () => call<{ checks: PcCheckCard[] }>('/checks', { method: 'GET' }, 'Could not load the checks');
export const fetchCheck = (id: string) =>
  call<PcCheckDetail>(`/checks/${id}`, { method: 'GET' }, 'Could not load that check');
export const fetchPcSpotlight = () => call<PcSpotlight>('/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchPcConfiguration = () =>
  call<PcConfiguration>('/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const fetchPcIntegrations = () =>
  call<{ integrations: PcIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load integrations');

/** Where a document can be read in the browser. */
export const documentUrl = (id: string) => buildApiUrl(`${prefix()}/documents/${id}/file`);

/** Open a document in a new tab, carrying the sign-in with it. */
export async function openDocument(id: string): Promise<void> {
  const response = await fetch(documentUrl(id), { headers: authHeaders(false) });
  if (!response.ok) throw new Error('Could not open that document');
  const url = URL.createObjectURL(await response.blob());
  window.open(url, '_blank', 'noopener');
  // The tab holds its own copy; this only releases ours.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Upload one policy's documents. They are read in the background. */
export async function startCheck(files: File[]): Promise<PcCheckDetail> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const response = await fetch(buildApiUrl(`${prefix()}/checks`), {
    method: 'POST',
    headers: authHeaders(false),   // the browser sets the multipart boundary itself
    body: form,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not upload those documents');
  }
  return response.json();
}

/** Listen for "the checks changed" until `signal` aborts. Carries no data. */
export async function watchChecks(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/checks/stream`), {
    headers: { ...authHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}

export async function downloadChecks(): Promise<string> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const response = await fetch(buildApiUrl(`${prefix()}/export.xlsx?${new URLSearchParams({ list: 'checks', tz })}`), {
    headers: authHeaders(false),
  });
  if (!response.ok) throw new Error('Could not download the checks');
  const filename = response.headers.get('Content-Disposition')?.match(/filename=["']?([^"';]+)/)?.[1]
    ?? 'policy-checking-checks.xlsx';
  const link = document.createElement('a');
  link.href = URL.createObjectURL(await response.blob());
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}
